<script setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useEvaluationStore } from '@/stores/useEvaluationStore'
import {
  KIND_BADGES,
  adjustmentColorClass,
  byAbsScore,
  displayValue,
  expandableKeys,
  filterTree,
  interactionValueFor,
} from '@/services/contributionTree'

// "Võrdlustehingu analüüs" — per-comparable contribution tree (RED-262).
// Three levels: adjustment group → base feature (one-hot families collapsed)
// → value token. Columns: subject value, comparable value, and THIS
// comparable's pairwise contribution. Group rows read their Mõju straight
// from grouped_differences so the visible totals always equal the
// Kohandused tab's bars. Enrichment (Estonian labels, score/interaction
// kinds, effective subject values) comes from subject_contributions and is
// feature-detected per row — old saved predictions degrade, never blank.
const props = defineProps({
  comparableId: { type: [Number, String], default: null },
})

const visible = defineModel('visible', { type: Boolean, default: false })

const { t, te } = useI18n()
const evaluationStore = useEvaluationStore()
const prediction = computed(() => evaluationStore.prediction)

const filter = ref('')
watch(visible, () => {
  filter.value = ''
})

// ─── enrichment lookups (subject_contributions is optional) ──────────────────

const byFeature = computed(() =>
  Object.fromEntries(
    (prediction.value?.subject_contributions?.items ?? []).map(item => [item.feature, item]),
  ),
)
const subjectValues = computed(() => prediction.value?.subject_contributions?.values ?? null)
// This comparable's effective model-input row — carries the server-derived
// values (imputed *_score_bkt & co) the DB-backed original_values never has.
const comparableValues = computed(
  () => prediction.value?.subject_contributions?.comparable_values?.[String(props.comparableId)] ?? null,
)

const effectiveValueFor = feature => {
  const value = subjectValues.value?.[feature]
  return value != null && value !== '' ? displayValue(value) : '–'
}

const comparableEffectiveFor = feature => {
  const value = comparableValues.value?.[feature]
  return value != null && value !== '' ? displayValue(value) : '–'
}

// The dialog's group keys are technical (asukoha_kohandus); the shaper's item
// groups are the ML side's Estonian names, which equal feature_groups[].label.
const groupKeyByLabel = computed(() => {
  const map = {}
  for (const [groupKey, groupInfo] of Object.entries(prediction.value?.feature_groups ?? {})) {
    if (groupInfo?.label) {
      map[groupInfo.label] = groupKey
    }
  }
  return map
})

// Interaction pair terms never appear in top_contributions (base features
// only) — surface them from subject_contributions inside their mapped group,
// badge-explained: their per-comparable mass is Shapley-split into the
// group's total and cannot be itemised per row.
const interactionsByGroupKey = computed(() => {
  const map = {}
  for (const item of prediction.value?.subject_contributions?.items ?? []) {
    if (item.kind !== 'interaction') {
      continue
    }
    const groupKey = groupKeyByLabel.value[item.group]
    if (groupKey) {
      ;(map[groupKey] ??= []).push(item)
    }
  }
  return map
})

const baseLabelFor = (base, items) => {
  const meta = byFeature.value[items?.[0]?.feature]
  if (meta && meta.base === base && meta.label !== meta.feature) {
    return meta.label
  }
  return te(`messages.transactions.ehr_fields.${base}`) ? t(`messages.transactions.ehr_fields.${base}`) : base
}

const originalValueFor = (propertyId, groupKey, names) => {
  const entries = prediction.value?.original_values?.[propertyId]?.[groupKey]
  if (!Array.isArray(entries)) {
    return null
  }
  for (const name of names) {
    const entry = entries.find(e => e?.name === name)
    if (entry && entry.value != null && entry.value !== '') {
      return displayValue(entry.value)
    }
  }
  return null
}

const formatContribution = value =>
  value == null ? null : `${value > 0 ? '+' : ''}${value.toFixed(2)}%`

// ─── tree building (per-comparable) ──────────────────────────────────────────

const leafNode = (item, groupKey, targetId, indented) => {
  const feature = item.feature
  const meta = byFeature.value[feature]
  const base = item.original_column || feature
  const isScore = meta?.kind === 'score' || feature.endsWith('_score_bkt')
  // Score rows without an effective row on the wire (older ml-api): fall back
  // to the family's token string from original_values as a grey hint.
  const scoreFamilyNames = isScore
    ? [feature.replace(/_score_bkt$/, ''), `kinnistu_${feature.replace(/_score_bkt$/, '')}`]
    : []
  const contribution = Number(item.contribution)
  const comparableEffective = comparableValues.value?.[feature]

  return {
    key: `f:${groupKey}:${feature}`,
    data: {
      label: indented
        ? (meta?.token ?? item.original_value_token ?? item.label ?? feature)
        : (meta?.label && meta.label !== feature ? meta.label : baseLabelFor(base, [item])),
      mono: indented,
      feature,
      badge: KIND_BADGES[meta?.kind] ?? (isScore ? KIND_BADGES.score : null),
      targetValue:
        meta?.value != null
          ? displayValue(meta.value)
          : subjectValues.value?.[feature] != null
            ? displayValue(subjectValues.value[feature])
            : originalValueFor(targetId, groupKey, [base, feature]),
      targetDetail: meta?.value_detail ?? null,
      comparableValue:
        comparableEffective != null && comparableEffective !== ''
          ? displayValue(comparableEffective)
          : originalValueFor(props.comparableId, groupKey, [base, feature, ...scoreFamilyNames]),
      score: Number.isFinite(contribution) ? contribution : 0,
      contribution: Number.isFinite(contribution) ? formatContribution(contribution) : null,
    },
  }
}

const familyNode = (base, items, groupKey, targetId) => {
  const sum = items.reduce((acc, item) => acc + (Number(item.contribution) || 0), 0)
  const children = items.map(item => leafNode(item, groupKey, targetId, true))
  children.sort(byAbsScore)
  return {
    key: `b:${groupKey}:${base}`,
    data: {
      label: baseLabelFor(base, items),
      feature: base,
      badge: null,
      targetValue:
        originalValueFor(targetId, groupKey, [base]) ??
        (subjectValues.value?.[base] != null ? displayValue(subjectValues.value[base]) : null),
      comparableValue: originalValueFor(props.comparableId, groupKey, [base]),
      score: sum,
      contribution: formatContribution(sum),
    },
    children,
  }
}

const interactionNode = (item, groupKey) => ({
  key: `x:${groupKey}:${item.feature}`,
  data: {
    label: item.feature,
    mono: true,
    feature: item.feature,
    badge: KIND_BADGES.interaction,
    targetValue: interactionValueFor(item.feature, effectiveValueFor),
    comparableValue: comparableValues.value
      ? interactionValueFor(item.feature, comparableEffectiveFor)
      : null,
    // Sorted to the group's tail — the per-comparable split is inside the
    // group total, not an itemisable row value.
    score: 0,
    contribution: null,
    contributionTitle:
      'Koosmõju osa jaotub Shapley-jaotusega grupi kogukohandusse ega ole rea kaupa eristatav.',
  },
})

const tree = computed(() => {
  if (!props.comparableId || !prediction.value) {
    return []
  }
  const comparableId = props.comparableId
  const targetId = prediction.value.target_property_id
  const featureGroups = prediction.value.feature_groups ?? {}

  const nodes = []
  for (const [groupKey, groupInfo] of Object.entries(featureGroups)) {
    const contributions = prediction.value.top_contributions?.[comparableId]?.[groupKey] ?? []
    const interactions = interactionsByGroupKey.value[groupKey] ?? []
    if (!contributions.length && !interactions.length) {
      continue
    }

    const byBase = new Map()
    for (const item of contributions) {
      const base = item.original_column || item.feature
      if (!byBase.has(base)) {
        byBase.set(base, [])
      }
      byBase.get(base).push(item)
    }

    const children = [...byBase.entries()].map(([base, items]) =>
      items.length === 1 && !items[0].original_value_token
        ? leafNode(items[0], groupKey, targetId, false)
        : familyNode(base, items, groupKey, targetId),
    )
    children.sort(byAbsScore)
    children.push(...interactions.map(item => interactionNode(item, groupKey)))

    const adjustment = Number(prediction.value.grouped_differences?.[comparableId]?.[groupKey])
    nodes.push({
      key: `g:${groupKey}`,
      data: {
        label: groupInfo?.label ?? groupKey,
        isGroup: true,
        targetValue: null,
        comparableValue: null,
        // The group Mõju comes straight from grouped_differences — the same
        // number the Kohandused tab's bar shows, never a re-summed one.
        score: Number.isFinite(adjustment) ? adjustment : 0,
        contribution: Number.isFinite(adjustment) ? formatContribution(adjustment) : null,
        badge: null,
      },
      children,
    })
  }
  nodes.sort(byAbsScore)
  return nodes
})

const visibleTree = computed(() => {
  const needle = filter.value.trim().toLowerCase()
  return needle ? filterTree(tree.value, needle) : tree.value
})

const expandedKeys = ref({})
watch(visibleTree, nodes => {
  if (!filter.value.trim()) {
    return
  }
  expandedKeys.value = expandableKeys(nodes)
})

// ─── tab 2: per-group adjustment bars (unchanged behaviour) ──────────────────

const adjustments = computed(() => {
  if (!props.comparableId || !prediction.value) {
    return {}
  }
  const grouped = prediction.value.grouped_differences?.[props.comparableId] || {}
  const maxAbs = Object.values(grouped)
    .map(Number)
    .filter(Number.isFinite)
    .reduce((max, value) => Math.max(max, Math.abs(value)), 0)

  const result = {}
  for (const groupKey in prediction.value.feature_groups ?? {}) {
    const value = Number(grouped[groupKey]) || 0
    result[groupKey] = {
      label: prediction.value.feature_groups[groupKey]?.label ?? groupKey,
      value,
      width: maxAbs > 0 ? (Math.abs(value) / maxAbs) * 100 : 0,
    }
  }
  return result
})
</script>

<template>
  <Dialog v-model:visible="visible" :header="`Võrdlustehingu ${comparableId} analüüs`" modal :style="{ width: '75rem', maxWidth: '95vw' }">
    <Tabs value="0">
      <TabList>
        <Tab value="0">Tunnuste analüüs</Tab>
        <Tab value="1">Kohandused</Tab>
      </TabList>
      <TabPanels>
        <TabPanel value="0">
          <div class="flex flex-col gap-3">
            <div class="text-[11px] text-gray-500 leading-snug">
              <span class="font-bold text-green-500">Positiivsed väärtused</span> näitavad tunnuseid, kus hinnatav
              vara on parem kui võrdlustehing, <span class="font-bold text-red-500">negatiivsed</span> — kus
              võrdlustehing on parem. Grupid ja tunnused on järjestatud absoluutmõju järgi; grupi Mõju võrdub
              Kohandused-vaate tulbaga.
            </div>
            <IconField>
              <InputIcon class="pi pi-search" />
              <InputText v-model="filter" placeholder="Filtreeri tunnuseid…" size="small" class="w-full" />
            </IconField>
            <TreeTable
              v-model:expanded-keys="expandedKeys"
              :value="visibleTree"
              size="small"
              scrollable
              scroll-height="55vh"
              class="text-sm"
            >
              <Column header="Tunnus" expander style="width: 40%">
                <template #body="{ node }">
                  <span
                    :class="[
                      node.data.isGroup ? 'font-semibold' : node.data.mono ? 'font-mono text-xs' : 'text-xs',
                      'break-all',
                    ]"
                    :title="node.data.feature"
                    >{{ node.data.label }}</span
                  >
                  <Tag
                    v-if="node.data.badge"
                    :value="node.data.badge.value"
                    :severity="node.data.badge.severity"
                    :title="node.data.badge.title"
                    class="ml-2 text-[10px]!"
                  />
                </template>
              </Column>
              <Column header="Hinnatav vara" style="width: 22%">
                <template #body="{ node }">
                  <span v-if="node.data.targetValue != null" class="font-mono text-xs wrap-break-word">{{
                    node.data.targetValue
                  }}</span>
                  <span v-if="node.data.targetDetail" class="text-xs text-gray-400 ml-1"
                    >({{ node.data.targetDetail }})</span
                  >
                </template>
              </Column>
              <Column header="Võrdlustehing" style="width: 22%">
                <template #body="{ node }">
                  <span v-if="node.data.comparableValue != null" class="font-mono text-xs wrap-break-word">{{
                    node.data.comparableValue
                  }}</span>
                  <span
                    v-else-if="!node.data.isGroup"
                    class="text-xs text-gray-300"
                    title="Väärtus arvutatakse serveris võrdlustehingu toorandmetest"
                    >–</span
                  >
                </template>
              </Column>
              <Column header="Mõju" style="width: 16%">
                <template #body="{ node }">
                  <span
                    v-if="node.data.contribution != null"
                    class="text-xs font-medium tabular-nums"
                    :class="adjustmentColorClass(node.data.score, false, true)"
                    >{{ node.data.contribution }}</span
                  >
                  <span
                    v-else-if="!node.data.isGroup"
                    class="text-xs text-gray-300"
                    :title="node.data.contributionTitle"
                    >–</span
                  >
                </template>
              </Column>
            </TreeTable>
          </div>
        </TabPanel>
        <TabPanel value="1">
          <div class="flex flex-col gap-4">
            <div v-for="(groupData, groupKey) in adjustments" :key="groupKey" class="adjustment-group">
              <div class="flex justify-between items-center mb-1">
                <span class="font-semibold text-sm">{{ groupData.label }}</span>
                <span :class="['font-bold text-sm', adjustmentColorClass(groupData.value, false, true)]">
                  {{ groupData.value > 0 ? '+' : '' }}{{ groupData.value.toFixed(2) }}%
                </span>
              </div>
              <div class="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                <div
                  class="h-2.5 rounded-full"
                  :class="adjustmentColorClass(groupData.value, true, true)"
                  :style="{ width: groupData.width + '%' }"
                ></div>
              </div>
            </div>
          </div>
        </TabPanel>
      </TabPanels>
    </Tabs>
  </Dialog>
</template>
